import React from "react";
import {
    Box,
    Flex,
    Text,
    useColorModeValue,
    HStack,
    VStack,
    IconButton,
    Badge,
    Icon,
    SimpleGrid,
} from "@chakra-ui/react";
import {
    WiCloudy,
    WiDaySunny,
    WiNa,
    WiNightClear,
    WiRain,
    WiHumidity,
    WiStrongWind,
    WiRaindrop,
} from 'react-icons/wi';
import { FaRegSnowflake, FaRegStar, FaStar, FaChevronRight } from "react-icons/fa";
import { TbWind } from "react-icons/tb";
import { Link } from "react-router-dom";

const degreesToCardinal = (dir) => {
    if (dir === null || dir === undefined) return '-';
    let directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW', 'N'];
    return directions[Math.round(dir / 22.5)];
};

const getWeatherCondition = (station) => {
    if (!station.last_data || station.last_data.temp === null) return 'none';
    if (station.last_data.temp < 0) return 'freezing';
    if (station.last_data.precip_rate > 0) return 'rain';
    if (station.last_data.precip_total > 0) return 'cloudy';
    return 'clear';
};

// Weather icon based on conditions
const WeatherIcon = ({ station }) => {
    const condition = getWeatherCondition(station);
    const hours = new Date().getHours();
    const isNight = hours < 6 || hours > 18;
    const iconStyle = { fontSize: "2.2rem" };

    switch (condition) {
        case "freezing":
            return <FaRegSnowflake style={{ ...iconStyle, fontSize: "1.8rem" }} />;
        case "rain":
            return <WiRain style={iconStyle} />;
        case "cloudy":
            return <WiCloudy style={iconStyle} />;
        case "none":
            return <WiNa style={iconStyle} />;
        default:
            return isNight ? <WiNightClear style={iconStyle} /> : <WiDaySunny style={iconStyle} />;
    }
};

// Mini stat component with bigger icons and smaller labels
const MiniStat = ({ icon, value, unit, label, color, textPrimary, textSecondary, textMuted, subValue }) => (
    <VStack spacing={0} align="center" flex={1} minW={0}>
        <Icon as={icon} boxSize={8} color={color || textSecondary} mb={-0.5} />
        <HStack spacing={0} align="baseline">
            <Text fontSize="xs" fontWeight="semibold" color={textPrimary} lineHeight={1}>
                {value}
            </Text>
            {unit && <Text fontSize="10px" color={textMuted}>{unit}</Text>}
        </HStack>
        {subValue && (
            <Text fontSize="10px" color={textSecondary} lineHeight={1}>
                {subValue}
            </Text>
        )}
        <Text fontSize="10px" color={textMuted} textTransform="uppercase" letterSpacing="wide">
            {label}
        </Text>
    </VStack>
);

function Widget({ station, isFavorite, toggleFavorite }) {
    // Theme colors
    const cardBg = useColorModeValue("white", "gray.800");
    const cardBorder = useColorModeValue("gray.200", "gray.700");
    const textPrimary = useColorModeValue("gray.800", "white");
    const textSecondary = useColorModeValue("gray.500", "gray.400");
    const textMuted = useColorModeValue("gray.400", "gray.500");
    const tempBarBg = useColorModeValue("gray.50", "gray.700");
    const tempBarTrack = useColorModeValue("gray.200", "gray.600");
    const favBtnHoverBg = useColorModeValue("gray.100", "gray.700");
    const statsBg = useColorModeValue("gray.50", "gray.750");
    const shadowBase = useColorModeValue(
        "0 2px 8px rgba(0, 0, 0, 0.08)",
        "0 2px 8px rgba(0, 0, 0, 0.3)"
    );
    const shadowHover = useColorModeValue(
        "0 8px 25px rgba(0, 0, 0, 0.12)",
        "0 8px 25px rgba(0, 0, 0, 0.4)"
    );

    // Get temperature-based accent color
    const getAccentColor = () => {
        if (!station.last_data || station.last_data.temp === null) return "gray.400";
        const temp = station.last_data.temp;
        if (temp < 0) return "purple.500";
        if (temp < 10) return "blue.500";
        if (temp < 18) return "cyan.500";
        if (temp < 25) return "green.500";
        if (temp < 30) return "yellow.500";
        if (temp < 35) return "orange.500";
        return "red.500";
    };

    const accentColor = getAccentColor();

    return (
        <Box
            bg={cardBg}
            borderRadius="xl"
            overflow="hidden"
            transition="all 0.25s ease"
            boxShadow={shadowBase}
            border="2px solid"
            borderTop="6px solid"
            borderColor={accentColor}
            position="relative"
            _hover={{
                transform: "translateY(-4px)",
                boxShadow: shadowHover,
            }}
        >
            {/* Favorite Button */}
            <IconButton
                icon={isFavorite ? <FaStar /> : <FaRegStar />}
                aria-label={isFavorite ? "Remover favorito" : "Adicionar favorito"}
                position="absolute"
                top={3}
                right={3}
                size="sm"
                variant="ghost"
                color={isFavorite ? "yellow.400" : textMuted}
                _hover={{
                    bg: favBtnHoverBg,
                    color: "yellow.400",
                }}
                onClick={(e) => {
                    e.preventDefault();
                    toggleFavorite(station);
                }}
                zIndex={2}
            />

            <Link to={`/estacao/${station.id}`}>
                <Box p={4} pb={3}>
                    {/* Header - Time & Location */}
                    <VStack spacing={0.5} mb={2}>
                        {station.last_data && (
                            <Text fontSize="2xs" color={textMuted}>
                                {station.last_data.created_at}
                            </Text>
                        )}
                        <Text
                            fontWeight="bold"
                            fontSize="md"
                            color={textPrimary}
                            textAlign="center"
                            noOfLines={1}
                        >
                            {station.city}
                        </Text>
                        <Badge
                            colorScheme="gray"
                            variant="subtle"
                            fontSize="2xs"
                            borderRadius="full"
                            px={2}
                        >
                            {station.state}
                        </Badge>
                    </VStack>

                    {/* Main Temperature & Weather Icon */}
                    <Flex align="center" justify="center" mb={2} gap={3}>
                        <Flex
                            align="center"
                            justify="center"
                            bg={accentColor}
                            color="white"
                            borderRadius="full"
                            w="60px"
                            h="60px"
                            boxShadow="md"
                        >
                            <WeatherIcon station={station} />
                        </Flex>
                        {station.last_data ? (
                            <VStack spacing={0} align="flex-start">
                                <Text
                                    fontSize="4xl"
                                    fontWeight="bold"
                                    color={accentColor}
                                    lineHeight={1}
                                >
                                    {station.last_data.temp}
                                    <Text as="span" fontSize="lg" color={textSecondary}>°</Text>
                                </Text>
                                <Text fontSize="10px" color={textMuted}>
                                    Sensação {station.last_data.heat_index || station.last_data.temp}°
                                </Text>
                            </VStack>
                        ) : (
                            <Text fontSize="2xl" color={textMuted}>--</Text>
                        )}
                    </Flex>

                    {/* Min/Max Temperature Bar */}
                    {station.last_data && (
                        <Flex
                            justify="space-between"
                            align="center"
                            bg={tempBarBg}
                            borderRadius="lg"
                            px={3}
                            py={1.5}
                            mb={2}
                        >
                            <VStack spacing={0}>
                                <Text fontSize="xs" color="blue.400" fontWeight="semibold">
                                    {station.last_data.temp_min}°
                                </Text>
                                <Text fontSize="10px" color={textMuted}>MÍN</Text>
                            </VStack>
                            <Box
                                flex={1}
                                mx={3}
                                h="3px"
                                borderRadius="full"
                                bg={tempBarTrack}
                                position="relative"
                                overflow="hidden"
                            >
                                <Box
                                    position="absolute"
                                    left="0"
                                    top="0"
                                    h="full"
                                    w={`${Math.min(100, Math.max(0, ((station.last_data.temp - (station.last_data.temp_min || 0)) / ((station.last_data.temp_max || 40) - (station.last_data.temp_min || 0))) * 100))}%`}
                                    bg={`linear-gradient(90deg, #63B3ED, ${accentColor})`}
                                    borderRadius="full"
                                />
                            </Box>
                            <VStack spacing={0}>
                                <Text fontSize="xs" color="red.400" fontWeight="semibold">
                                    {station.last_data.temp_max}°
                                </Text>
                                <Text fontSize="10px" color={textMuted}>MÁX</Text>
                            </VStack>
                        </Flex>
                    )}

                    {/* Weather Stats Grid - All Data */}
                    {station.last_data && (
                        <Box
                            bg={statsBg}
                            borderRadius="lg"
                            p={2}
                        >
                            {/* First Row: Humidity, Rain Rate, Rain Total */}
                            <SimpleGrid columns={3} gap={1} mb={2}>
                                <MiniStat
                                    icon={WiHumidity}
                                    value={station.last_data.humidity || '-'}
                                    unit="%"
                                    label="Umidade"
                                    color="blue.400"
                                    textPrimary={textPrimary}
                                    textSecondary={textSecondary}
                                    textMuted={textMuted}
                                />
                                <MiniStat
                                    icon={WiRain}
                                    value={station.last_data.precip_rate || 0}
                                    unit="mm/h"
                                    label="Chuva"
                                    color="blue.500"
                                    textPrimary={textPrimary}
                                    textSecondary={textSecondary}
                                    textMuted={textMuted}
                                />
                                <MiniStat
                                    icon={WiRaindrop}
                                    value={station.last_data.precip_total || 0}
                                    unit="mm"
                                    label="Acumulado"
                                    color="blue.600"
                                    textPrimary={textPrimary}
                                    textSecondary={textSecondary}
                                    textMuted={textMuted}
                                />
                            </SimpleGrid>

                            {/* Second Row: Wind with Direction, Wind Gust */}
                            <SimpleGrid columns={2} gap={1}>
                                <MiniStat
                                    icon={WiStrongWind}
                                    value={`${station.last_data.wind_speed || 0}`}
                                    unit={`km/h (${degreesToCardinal(station.last_data.wind_dir)})`}
                                    label="Vento"
                                    color="cyan.500"
                                    textPrimary={textPrimary}
                                    textSecondary={textSecondary}
                                    textMuted={textMuted}
                                />
                                <MiniStat
                                    icon={TbWind}
                                    value={station.last_data.wind_gust || '-'}
                                    unit="km/h"
                                    label="Rajada"
                                    color="teal.400"
                                    textPrimary={textPrimary}
                                    textSecondary={textSecondary}
                                    textMuted={textMuted}
                                />
                            </SimpleGrid>
                        </Box>
                    )}

                    {/* View More Footer */}
                    <Flex
                        justify="center"
                        align="center"
                        mt={2}
                        pt={2}
                        borderTop="1px dashed"
                        borderColor={cardBorder}
                        color={textMuted}
                        fontSize="xs"
                    >
                        <Text>Ver detalhes</Text>
                        <Icon as={FaChevronRight} boxSize={2} ml={1} />
                    </Flex>
                </Box>
            </Link>
        </Box>
    );
}

export default React.memo(Widget);
